import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { ArrowLeft, Beaker, Check, X, Undo2, Play, Plus, Trash2, RefreshCw, Shield } from "lucide-react";
import { toast } from "sonner";

const BASE = "/business-coach/notifications";

const STATUS_CLS = {
  proposed: "bg-zinc-100 text-zinc-700 border-zinc-200",
  tested: "bg-sky-100 text-sky-700 border-sky-200",
  approved: "bg-emerald-100 text-emerald-700 border-emerald-200",
  rejected: "bg-rose-100 text-rose-700 border-rose-200",
  rolled_back: "bg-amber-100 text-amber-700 border-amber-200",
};

export default function CoachRulesLab() {
  const nav = useNavigate();
  const [report, setReport] = useState(null);
  const [proposals, setProposals] = useState([]);
  const [active, setActive] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});
  const [testResults, setTestResults] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const [rep, props, cur, hist] = await Promise.all([
        api.get(`${BASE}/corrections/report`),
        api.get(`${BASE}/rules/proposals`),
        api.get(`${BASE}/rules/current`),
        api.get(`${BASE}/rules/history`),
      ]);
      setReport(rep.data);
      setProposals(props.data || []);
      setActive(cur.data);
      setHistory(hist.data || []);
    } catch (e) {
      toast.error("Failed to load rules lab: " + (e?.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const proposeFromInsight = async (ins) => {
    const p = ins.proposed_rule || {};
    if (!p.patterns || p.patterns.length === 0) {
      toast.error("No patterns detected — edit the rule manually below after proposing.");
    }
    try {
      const { data } = await api.post(`${BASE}/rules/proposals`, {
        from_pair: ins.pair,
        original_intent: null,
        original_sentiment: ins.original_sentiment,
        target_intent: p.target_intent || "other",
        target_sentiment: p.target_sentiment || "neutral",
        patterns: p.patterns && p.patterns.length ? p.patterns : ["<edit-me>"],
        match_mode: p.match_mode || "contains",
        confidence: p.confidence || 0.75,
        reason: p.reason,
      });
      toast.success("Proposal created (draft). Test before approve.");
      setProposals((prev) => [data, ...prev]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create proposal");
    }
  };

  const editPattern = async (id, patterns, patch = {}) => {
    try {
      const { data } = await api.patch(`${BASE}/rules/proposals/${id}`, {
        patterns, ...patch,
      });
      setProposals((prev) => prev.map((p) => (p.id === id ? data : p)));
      toast.success("Proposal updated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Edit failed");
    }
  };

  const runTest = async (id) => {
    try {
      const { data } = await api.post(`${BASE}/rules/proposals/${id}/test`);
      setProposals((prev) => prev.map((p) => (p.id === id ? data : p)));
      setTestResults((prev) => ({ ...prev, [id]: data.test_summary }));
      toast.success(`Tested against ${data.test_summary?.corpus_size ?? 0} replies`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Test failed");
    }
  };

  const approve = async (id) => {
    const reason = window.prompt("Approval reason (required for audit)");
    if (!reason) return;
    try {
      const { data } = await api.post(`${BASE}/rules/proposals/${id}/approve`, { reason });
      setProposals((prev) => prev.map((p) => (p.id === id ? data : p)));
      toast.success(`Approved as version ${data.activated_version_number}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Approve failed");
    }
  };

  const reject = async (id) => {
    const reason = window.prompt("Reject reason");
    if (!reason) return;
    try {
      const { data } = await api.post(`${BASE}/rules/proposals/${id}/reject`, { reason });
      setProposals((prev) => prev.map((p) => (p.id === id ? data : p)));
      toast.info("Proposal rejected");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reject failed");
    }
  };

  const rollback = async (id) => {
    const reason = window.prompt("Rollback reason");
    if (!reason) return;
    try {
      const { data } = await api.post(`${BASE}/rules/proposals/${id}/rollback`, { reason });
      setProposals((prev) => prev.map((p) => (p.id === id ? data : p)));
      toast.success("Rolled back to previous version");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Rollback failed");
    }
  };

  return (
    <div className="p-6 md:p-8 max-w-7xl" data-testid="coach-rules-lab">
      <button
        onClick={() => nav("/app/coach")}
        data-testid="rules-back"
        className="flex items-center gap-1 text-sm text-zinc-500 hover:text-[#FF5A00] mb-3"
      >
        <ArrowLeft className="w-4 h-4"/> Back to Coach
      </button>
      <div className="flex items-baseline justify-between mb-6">
        <div>
          <div className="text-xs font-mono-accent uppercase tracking-widest text-[#FF5A00] mb-1">
            Rules Lab
          </div>
          <h1 className="font-heading text-3xl font-bold">Corrections insights &amp; one-tap rule edits</h1>
          <p className="text-sm text-zinc-500 mt-1">
            Turn the most-corrected classifier mistakes into tenant-scoped rules. Nothing goes live until you dry-run and approve.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={load} data-testid="rules-refresh">
          <RefreshCw className="w-3 h-3 mr-1"/> Refresh
        </Button>
      </div>

      <Card className="p-4 mb-6 border-orange-200 bg-orange-50" data-testid="rules-classifier-summary">
        <div className="flex items-center gap-3">
          <Shield className="w-5 h-5 text-orange-700"/>
          <div>
            <div className="text-xs uppercase tracking-wider text-orange-700 font-mono-accent">Active classifier</div>
            <div className="text-sm font-semibold" data-testid="rules-active-version">
              {active?.version_number ? `Version ${active.version_number} · ${active.rules?.length || 0} tenant rule(s)` : "Default rules only (no tenant overlay)"}
            </div>
            <div className="text-[11px] text-zinc-500">
              Consent rules (STOP, wrong-person) are protected and cannot be modified.
            </div>
          </div>
        </div>
      </Card>

      <h2 className="font-heading text-xl font-bold mb-3">Top correction pairs</h2>
      {loading && <div className="text-sm text-zinc-500">Loading…</div>}
      {!loading && (!report?.insights || report.insights.length === 0) && (
        <Card className="p-6 text-center text-zinc-500" data-testid="rules-insights-empty">
          No corrections yet. As you fix classifications, patterns will surface here.
        </Card>
      )}
      <div className="space-y-3 mb-8">
        {(report?.insights || []).map((ins, i) => (
          <Card key={i} className="p-4" data-testid={`insight-card-${i}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <span className="font-mono text-sm bg-zinc-100 px-2 py-0.5 rounded">{ins.pair}</span>
                  <span className="text-xs text-zinc-500">{ins.count} corrections</span>
                  <span className="text-xs text-rose-600">{ins.error_rate_percent}% error rate</span>
                  {ins.avg_confidence != null && (
                    <span className="text-xs text-zinc-500">avg confidence {ins.avg_confidence}</span>
                  )}
                </div>
                <div className="text-sm mt-1">
                  <b>Most common corrected intent:</b> <span className="font-mono">{ins.top_corrected_intent}</span>
                </div>
                {ins.examples?.length > 0 && (
                  <details className="mt-2 text-xs text-zinc-600">
                    <summary className="cursor-pointer">Redacted examples ({ins.examples.length})</summary>
                    <ul className="mt-1 space-y-1">
                      {ins.examples.map((e, j) => (
                        <li key={j} className="border-l-2 border-orange-300 pl-2 italic">"{e.body_excerpt}"</li>
                      ))}
                    </ul>
                  </details>
                )}
                <div className="mt-2 text-xs text-zinc-700 bg-zinc-50 border border-zinc-200 rounded p-2">
                  <b>Proposed rule:</b> when body {ins.proposed_rule?.match_mode || "contains"} one of{" "}
                  <span className="font-mono">{JSON.stringify(ins.proposed_rule?.patterns || [])}</span>{" "}
                  → classify as{" "}
                  <span className="font-mono">{ins.proposed_rule?.target_intent}</span> /{" "}
                  <span className="font-mono">{ins.proposed_rule?.target_sentiment}</span>
                  <div className="mt-1 text-[11px] italic text-zinc-500">{ins.estimated_impact_note}</div>
                </div>
              </div>
              <Button
                onClick={() => proposeFromInsight(ins)}
                className="bg-[#FF5A00] hover:bg-[#E04F00] text-white"
                data-testid={`propose-rule-btn-${i}`}
                size="sm"
              >
                <Plus className="w-3 h-3 mr-1"/> Propose rule
              </Button>
            </div>
          </Card>
        ))}
      </div>

      <h2 className="font-heading text-xl font-bold mb-3">Proposals</h2>
      {proposals.length === 0 ? (
        <Card className="p-6 text-center text-zinc-500" data-testid="rules-proposals-empty">
          No proposals yet. Create one from an insight above.
        </Card>
      ) : (
        <div className="space-y-3">
          {proposals.map((p) => (
            <ProposalCard
              key={p.id}
              p={p}
              expanded={!!expanded[p.id]}
              onToggle={() => setExpanded({ ...expanded, [p.id]: !expanded[p.id] })}
              onEdit={editPattern}
              onTest={runTest}
              onApprove={approve}
              onReject={reject}
              onRollback={rollback}
            />
          ))}
        </div>
      )}

      {history.length > 0 && (
        <>
          <h2 className="font-heading text-xl font-bold mt-8 mb-3">Version history</h2>
          <Card className="p-3">
            <table className="w-full text-sm" data-testid="rules-history-table">
              <thead className="text-xs text-zinc-500 uppercase">
                <tr>
                  <th className="text-left px-2 py-1">Version</th>
                  <th className="text-left px-2 py-1">Active</th>
                  <th className="text-left px-2 py-1">Activated at</th>
                  <th className="text-left px-2 py-1">Rules</th>
                </tr>
              </thead>
              <tbody>
                {history.map((h) => (
                  <tr key={h.version_id} className="border-t border-zinc-100">
                    <td className="px-2 py-1 font-mono">v{h.version_number}</td>
                    <td className="px-2 py-1">{h.active ? "✓" : "—"}</td>
                    <td className="px-2 py-1 text-xs text-zinc-500">{(h.activated_at || "").replace("T", " ").slice(0, 16)}</td>
                    <td className="px-2 py-1">{h.rule_count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </>
      )}
    </div>
  );
}

function ProposalCard({ p, expanded, onToggle, onEdit, onTest, onApprove, onReject, onRollback }) {
  const [patterns, setPatterns] = useState((p.patterns || []).join(", "));
  const [reason, setReason] = useState(p.reason || "");
  const editable = p.status === "proposed" || p.status === "tested";
  const canTest = editable;
  const canApprove = p.status === "tested";
  const canReject = editable;
  const canRollback = p.status === "approved";

  const saveEdits = async () => {
    const parts = patterns.split(",").map((s) => s.trim()).filter(Boolean);
    await onEdit(p.id, parts, { reason });
  };

  return (
    <Card className="p-4" data-testid={`proposal-card-${p.id}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border ${STATUS_CLS[p.status] || "bg-zinc-100"}`}
                data-testid={`proposal-status-${p.id}`}>
            {p.status}
          </span>
          <span className="font-mono text-xs">{p.from_pair}</span>
          <span className="text-xs text-zinc-500">
            → {p.target_intent} / {p.target_sentiment}
          </span>
        </div>
        <div className="flex gap-1">
          {canTest && (
            <Button size="sm" variant="outline" onClick={() => onTest(p.id)} data-testid={`proposal-test-${p.id}`}>
              <Play className="w-3 h-3 mr-1"/> Test
            </Button>
          )}
          {canApprove && (
            <Button size="sm" onClick={() => onApprove(p.id)} data-testid={`proposal-approve-${p.id}`}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white">
              <Check className="w-3 h-3 mr-1"/> Approve
            </Button>
          )}
          {canReject && (
            <Button size="sm" variant="outline" onClick={() => onReject(p.id)} data-testid={`proposal-reject-${p.id}`}>
              <X className="w-3 h-3 mr-1"/> Reject
            </Button>
          )}
          {canRollback && (
            <Button size="sm" variant="outline" onClick={() => onRollback(p.id)} data-testid={`proposal-rollback-${p.id}`}>
              <Undo2 className="w-3 h-3 mr-1"/> Rollback
            </Button>
          )}
          <Button size="sm" variant="ghost" onClick={onToggle} data-testid={`proposal-toggle-${p.id}`}>
            {expanded ? "Hide" : "Edit"}
          </Button>
        </div>
      </div>

      {p.impact_after && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs mb-2" data-testid={`proposal-impact-${p.id}`}>
          <Kpi label="Corpus" value={p.impact_after.matched != null ? "—" : "—"}
               overrideValue={`${(p.impact_after.matched ?? 0)} / ${(p.impact_after.matched_correct ?? 0)}`}/>
          <Kpi label="Precision" value={`${p.impact_after.precision_percent ?? "—"}%`}/>
          <Kpi label="Recall" value={`${p.impact_after.recall_percent ?? "—"}%`}/>
          <Kpi label="False positives" value={p.impact_after.false_positives ?? "—"}/>
        </div>
      )}

      {expanded && editable && (
        <div className="space-y-2 mt-3">
          <div>
            <label className="text-[10px] uppercase tracking-wider text-zinc-500">Patterns (comma-separated)</label>
            <Input value={patterns} onChange={(e) => setPatterns(e.target.value)}
                   data-testid={`proposal-patterns-${p.id}`}/>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-zinc-500">Reason</label>
            <Textarea value={reason} onChange={(e) => setReason(e.target.value)}
                        rows={2} data-testid={`proposal-reason-${p.id}`}/>
          </div>
          <div className="flex gap-2">
            <Button size="sm" onClick={saveEdits} data-testid={`proposal-save-${p.id}`}>Save edits</Button>
          </div>
        </div>
      )}

      {p.status === "approved" && (
        <div className="text-[11px] text-emerald-700 mt-2">
          Activated as version {p.activated_version_number} at {p.approved_at?.replace("T", " ").slice(0, 16)}
        </div>
      )}
      {p.status === "rejected" && (
        <div className="text-[11px] text-rose-700 mt-2">Rejected: {p.rejection_reason}</div>
      )}
      {p.status === "rolled_back" && (
        <div className="text-[11px] text-amber-700 mt-2">Rolled back: {p.rolled_back_reason}</div>
      )}
    </Card>
  );
}

function Kpi({ label, value, overrideValue }) {
  return (
    <div className="bg-zinc-50 border border-zinc-200 rounded p-2">
      <div className="text-[9px] uppercase tracking-wider text-zinc-500">{label}</div>
      <div className="text-sm font-bold">{overrideValue ?? value}</div>
    </div>
  );
}
